import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AnalysisResults } from '../types';

interface AnalysisState {
  jobDescription: string;
  files: File[];
  enrichResumes: boolean;
  results: AnalysisResults | null;
  sessionId: string;
  isLoading: boolean;
  error: string | null;
}

interface AnalysisContextType {
  state: AnalysisState;
  setJobDescription: (jobDescription: string) => void;
  setFiles: (files: File[]) => void;
  setEnrichResumes: (enrichResumes: boolean) => void;
  setResults: (results: any) => void;
  setSessionId: (sessionId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearState: () => void;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

const initialState: AnalysisState = {
  jobDescription: '',
  files: [],
  enrichResumes: true,
  results: null,
  sessionId: '',
  isLoading: false,
  error: null,
};

export const AnalysisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AnalysisState>(initialState);

  const setJobDescription = useCallback((jobDescription: string) => {
    setState(prev => ({ ...prev, jobDescription }));
  }, []);

  const setFiles = useCallback((files: File[]) => {
    setState(prev => ({ ...prev, files }));
  }, []);

  const setEnrichResumes = useCallback((enrichResumes: boolean) => {
    setState(prev => ({ ...prev, enrichResumes }));
  }, []);

  const setResults = useCallback((results: AnalysisResults) => {
    setState(prev => ({ ...prev, results }));
  }, []);

  const setSessionId = useCallback((sessionId: string) => {
    setState(prev => ({ ...prev, sessionId }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const clearState = useCallback(() => {
    setState(initialState);
  }, []);

  return (
    <AnalysisContext.Provider value={{
      state,
      setJobDescription,
      setFiles,
      setEnrichResumes,
      setResults,
      setSessionId,
      setLoading,
      setError,
      clearState,
    }}>
      {children}
    </AnalysisContext.Provider>
  );
};

export const useAnalysis = () => {
  const context = useContext(AnalysisContext);
  if (context === undefined) {
    throw new Error('useAnalysis must be used within an AnalysisProvider');
  }
  return context;
};